import React, { useContext, useState, useEffect } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { AuthContext } from "../../Contexts/AuthContext";

const Header = () => {
  const { currentUser, logout, refreshUserData } = useContext(AuthContext);
  const history = useHistory();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Get user role from currentUser context first, then fallback to localStorage
  const userRole =
    currentUser?.user_role ||
    currentUser?.role ||
    localStorage.getItem("user_role");


  // Handle scroll effect for header
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Refresh user data on component mount to get email for existing sessions
  useEffect(() => {
    if (currentUser && !currentUser.user_email_address) {
      refreshUserData();
    }
  }, [currentUser, refreshUserData]);

  const handleLogout = () => {
    logout();
    setUserMenuOpen(false);
    toggleUserMenu();
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const toggleUserMenu = () => {
    setUserMenuOpen(!userMenuOpen);
  };

  // Check if link matches current path for active styling
  const isActive = (path) => {
    if (path === "/") {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <header
      className={`sticky top-0 w-full z-50 transition-all duration-300 ease-in-out ${
        scrolled
          ? "bg-white shadow-sm py-2"
          : "bg-gradient-to-r from-orange-500 to-red-600 text-white py-2"
      }`}
      style={{
        display: "flex",
        justifyContent: "center",
        // keep the bar clear of a phone's status bar / notch
        paddingTop: "max(0.5rem, env(safe-area-inset-top))",
        alignItems: "center",
      }}
    >
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center">
          {/* Logo & Brand */}
          <div className="flex items-center space-x-2">
            <Link to="/" className="flex items-center space-x-2 group">
              {/* the Linguistic Communication lockup is portrait, so it sits in
                  a rounded chip rather than a circle that would crop it */}
              <div className="rounded-xl bg-white px-2 py-1 shadow-sm">
                <img
                  src="/lc-logo-navy.png"
                  alt="Linguistic Communication"
                  className="h-12 w-auto sm:h-16"
                />
              </div>
              <div className="flex flex-col">
                <span
                  className={`text-lg font-extrabold tracking-tight leading-none ${
                    scrolled
                      ? "bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent"
                      : "text-white drop-shadow-sm"
                  }`}
                >
                  SyntaxMap
                </span>
                <span
                  className={`hidden sm:block text-xs font-medium ${
                    scrolled ? "text-gray-600" : "text-orange-100"
                  }`}
                >
                  Master Grammar with Confidence
                </span>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation - More compact */}
          <nav className="hidden lg:flex items-center">
            <div className="flex space-x-3">
              {[
                { title: "Home", path: "/" },
                // authoring tools belong to whoever writes courses, not to students
                ...(userRole == 1
                  ? [
                      { title: "Add Course", path: "/addcourse" },
                      { title: "Add Question", path: "/addquestion" },
                    ]
                  : []),
                { title: "Notepad", path: "/notepad" },
                // multiplayer belongs to signed-in players only
                ...(currentUser
                  ? [{ title: "Multiplayer", path: "/multiplayer" }]
                  : []),
              ].map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-3 py-2 text-sm relative group ${
                    isActive(item.path)
                      ? scrolled
                        ? "font-semibold text-orange-600"
                        : "font-semibold text-white"
                      : scrolled
                      ? "text-gray-700 hover:text-orange-600"
                      : "text-orange-100 hover:text-white "
                  }`}
                >
                  {item.title}
                  <span
                    className={`absolute bottom-0 left-0 w-full h-0.5 transform origin-left transition-transform duration-300 ease-out ${
                      isActive(item.path)
                        ? scrolled
                          ? "bg-orange-500 scale-x-100"
                          : "bg-white scale-x-100"
                        : scrolled
                        ? "bg-orange-400 scale-x-0 group-hover:scale-x-100"
                        : "bg-white scale-x-0 group-hover:scale-x-100"
                    }`}
                  ></span>
                </Link>
              ))}
              {userRole == 1 && (
                <div className="relative group">
                  <button
                    className={`px-3 py-2 text-sm relative group ${
                      scrolled
                        ? "text-gray-700 hover:text-orange-600"
                        : "text-orange-100 hover:text-white hover:underline"
                    }`}
                  >
                    Admin
                  </button>
                  <div className="absolute left-0 mt-2 w-40 bg-white rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-20">
                    <Link
                      to="/admincourse"
                      className="block px-4 py-2 text-gray-800 hover:bg-orange-100"
                    >
                      Admin Course
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </nav>

          {/* User Authentication */}
          <div className="flex items-center space-x-2">
            {currentUser ? (
              <div className="relative">
                <button
                  onClick={toggleUserMenu}
                  className={`flex items-center space-x-1 px-2 min-h-[44px] text-sm font-medium whitespace-nowrap ${
                    scrolled
                      ? "text-gray-700 hover:text-orange-500"
                      : "text-white hover:text-orange-100"
                  }`}
                  aria-expanded={userMenuOpen}
                  aria-haspopup="true"
                >
                  <span className="max-w-[6.5rem] truncate">
                    {currentUser.user_name || "User"}
                  </span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-4 w-4 transition-transform duration-200 ${
                      userMenuOpen ? "rotate-180" : ""
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {/* User dropdown menu with animation */}
                <div
                  className={`absolute right-0 mt-1 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none transition-all duration-200 ease-in-out transform origin-top-right z-50 ${
                    userMenuOpen
                      ? "opacity-100 scale-100"
                      : "opacity-0 scale-95 pointer-events-none"
                  }`}
                  role="menu"
                  aria-orientation="vertical"
                  aria-labelledby="user-menu"
                >
                  {/* User menu items */}
                  <div className="py-1" role="none">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">
                        {currentUser.user_name || "User"}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {currentUser.user_email_address || "No email"}
                      </p>
                      <p className="text-xs text-gray-400">
                        Role:{" "}
                        {parseInt(userRole) === 1
                          ? "Admin"
                          : parseInt(userRole) === 2
                          ? "Teacher"
                          : parseInt(userRole) === 3
                          ? "Student"
                          : "Guest"}
                      </p>
                    </div>
                    <Link
                      to={
                        parseInt(userRole) === 1
                          ? "/admincontrolpanel"
                          : parseInt(userRole) === 2
                          ? "/notepad"
                          : parseInt(userRole) === 3
                          ? "/notepad"
                          : "/notepad"
                      }
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-700 transition-colors duration-150"
                      role="menuitem"
                    >
                      <div
                        className="flex items-center"
                        onClick={toggleUserMenu}
                      >
                        <svg
                          className="mr-2 h-4 w-4 text-orange-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                          ></path>
                        </svg>
                        Dashboard
                      </div>
                    </Link>
                    <Link
                      to="/profile"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-700 transition-colors duration-150"
                      role="menuitem"
                      onClick={toggleUserMenu}
                    >
                      <div className="flex items-center">
                        <svg
                          className="mr-2 h-4 w-4 text-orange-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z"
                          />
                        </svg>
                        Account settings
                      </div>
                    </Link>
                    {/* More menu items */}
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-150 border-t border-gray-100"
                      role="menuitem"
                    >
                      <div className="flex items-center">
                        <svg
                          className="mr-2 h-4 w-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                          ></path>
                        </svg>
                        Logout
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <Link
                  to="/login_register"
                  className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md ${
                    scrolled
                      ? "bg-orange-500 text-white"
                      : "bg-white text-orange-500"
                  }`}
                >
                  Sign In
                </Link>
              </>
            )}

            {/* Signed-in users already have Dashboard and Log out in the account
                menu above, so this call to action is for visitors only. */}
            {!currentUser && (
              <Link
                to="/login_register"
                className={`inline-flex items-center px-4 min-h-[44px] text-sm font-medium rounded-md shadow-sm ${
                  scrolled
                    ? "bg-gradient-to-r from-orange-500 to-red-500 text-white hover:from-orange-600 hover:to-red-600 transform transition-transform duration-300 hover:-translate-y-0.5"
                    : "bg-white text-orange-600 hover:bg-orange-50 transform transition-transform duration-300 hover:-translate-y-0.5"
                }`}
              >
              <span>Get started</span>
              <svg
                className="ml-1.5 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M14 5l7 7m0 0l-7 7m7-7H3"
                  ></path>
                  </svg>
                  </Link>
                  )}
                  </div>

          {/* Mobile menu button */}
          <div className="lg:hidden flex items-center">
            <button
              onClick={toggleMobileMenu}
              className={`p-2.5 -mr-1 rounded-md focus:outline-none ${
                scrolled ? "text-gray-700" : "text-white"
              }`}
              aria-expanded="false"
            >
              <span className="sr-only">Open main menu</span>
              {!mobileMenuOpen ? (
                <svg
                  className="block h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              ) : (
                <svg
                  className="block h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu, animated slide-in */}
        <div
          className={`lg:hidden transition-all duration-300 ease-in-out overflow-hidden ${
            mobileMenuOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <div
            className={`px-2 pt-2 pb-3 space-y-1 border-t ${
              scrolled ? "border-gray-200" : "border-orange-400"
            } mt-3`}
          >
            {[
              { title: "Home", path: "/" },
              ...(userRole == 1
                ? [
                    { title: "Add Course", path: "/addcourse" },
                    { title: "Add Question", path: "/addquestion" },
                  ]
                : []),
              { title: "Notepad", path: "/notepad" },
              ...(currentUser
                ? [{ title: "Multiplayer", path: "/multiplayer" }]
                : []),
              currentUser && currentUser.user_role === 1
                ? { title: "Admin Course", path: "/admincourse" }
                : null,
            ]
              .filter(Boolean)
              .map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`block px-3 py-2.5 rounded-md text-base relative ${
                    isActive(item.path)
                      ? scrolled
                        ? "bg-orange-50 text-orange-700 font-medium border-l-4 border-orange-500"
                        : "bg-white bg-opacity-20 text-white font-medium border-l-4 border-white"
                      : scrolled
                      ? "text-gray-700 hover:bg-gray-50 hover:text-orange-600"
                      : "text-white hover:bg-white hover:bg-opacity-10"
                  }`}
                >
                  {item.title}
                </Link>
              ))}

            {/* a signed-in user on a phone had no account or logout entry at all:
                the desktop account menu is hidden below lg */}
            {currentUser && (
              <div className="pt-4 pb-2 border-t border-orange-400">
                <div className="px-3 pb-2">
                  <p
                    className={`text-sm font-medium ${
                      scrolled ? "text-gray-900" : "text-white"
                    }`}
                  >
                    {currentUser.user_name || "User"}
                  </p>
                  <p
                    className={`text-xs truncate ${
                      scrolled ? "text-gray-500" : "text-orange-100"
                    }`}
                  >
                    {currentUser.user_email_address || ""}
                  </p>
                </div>
                <Link
                  to="/profile"
                  className={`block px-3 py-2.5 rounded-md text-base ${
                    scrolled
                      ? "text-gray-700 hover:bg-gray-50 hover:text-orange-600"
                      : "text-white hover:bg-white hover:bg-opacity-10"
                  }`}
                >
                  Account settings
                </Link>
                <Link
                  to={parseInt(userRole) === 1 ? "/admincontrolpanel" : "/notepad"}
                  className={`block px-3 py-2.5 rounded-md text-base ${
                    scrolled
                      ? "text-gray-700 hover:bg-gray-50 hover:text-orange-600"
                      : "text-white hover:bg-white hover:bg-opacity-10"
                  }`}
                >
                  Dashboard
                </Link>
                <button
                  onClick={handleLogout}
                  className={`block w-full text-left px-3 py-2.5 rounded-md text-base font-medium ${
                    scrolled
                      ? "text-red-600 hover:bg-red-50"
                      : "text-white hover:bg-white hover:bg-opacity-10"
                  }`}
                >
                  Log out
                </button>
              </div>
            )}

            {!currentUser && (
              <div className="pt-4 pb-2 border-t border-orange-400 flex flex-col space-y-2">
                <Link
                  to="/login_register"
                  className={`flex items-center px-3 py-2.5 rounded-md text-base font-medium transition-all duration-200 ${
                    scrolled
                      ? "text-gray-700 hover:bg-gray-50 hover:text-orange-600"
                      : "text-white hover:bg-white hover:bg-opacity-10"
                  }`}
                >
                  <svg
                    className="mr-2 h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                    ></path>
                  </svg>
                  Login
                </Link>
                <Link
                  to="/login_register"
                  className={`flex items-center px-3 py-2.5 rounded-md text-base font-medium transition-all duration-200 ${
                    scrolled
                      ? "bg-orange-500 text-white"
                      : "bg-white text-orange-500"
                  }`}
                >
                  <svg
                    className="mr-2 h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                    ></path>
                  </svg>
                  Sign Up
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Spacer to prevent content from hiding behind fixed header */}
      <div className="h-12"></div>
    </header>
  );
};

export default Header;
